/**
 * insight.tsx — the two blocks REDESIGN §3 and §4 put at the top of the home screen:
 * `מה קורה` (the written summary) and the one urgency row component that every bucket reuses.
 *
 * What this file owes `docs/insight-contract.md` §5:
 *  1. a `num` part is rendered inside its own `<bdi dir="ltr">` — printing `sentence.text` raw
 *     inside an RTL paragraph reorders `₪28,450` and `10.09`; `text` is for `aria-label` only;
 *  2. the narrative is a PLAIN surface (no card border, §8 "cards are earned"), headline at
 *     `--fk-fs-lede`, lines at `--fk-fs-body`, and it never adds or hides a sentence;
 *  3. buckets come from `urgency.groups` — the server already dropped the empty ones;
 *  4. the icon keys are semantic and mapped HERE, the one place to re-point;
 *  5. `item.countdown` is rendered as delivered, never recomputed;
 *  6. severity is state: a late row gets the left stripe, never a full red card.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { URGENCY_BUCKET_LABELS } from '../insight.ts';
import type { Narrative, NarrativeSentence, UrgencyBucket, UrgencyIcon, UrgencyItem } from '../insight.ts';
import { fmtMoney } from '../format.ts';
import { ddmm } from '../insight.ts';
import { he, plural } from '../i18n/he.ts';
import { Icon } from './Icon.tsx';
import { Chip } from './ui.tsx';
import { Link } from '../hooks/useRouter.tsx';
import { CategoryIcon as KitCategoryIcon, ProviderMark } from '../../../../shared/ui/src/index.tsx';
import type { GlyphName } from '../../../../shared/ui/src/index.tsx';

// ───────────────────────────── the narrative (§3) ─────────────────────────────

/** One sentence, with every numeric run isolated LTR (insight-contract §5.1). */
function Sentence({ sentence, className }: { sentence: NarrativeSentence; className: string }) {
  return (
    <p className={className}>
      {sentence.parts.map((part, index) =>
        part.kind === 'num' ? (
          <bdi key={index} dir="ltr" className="fk-num">
            {part.text}
          </bdi>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </p>
  );
}

/** `מה קורה` — the block above every figure on the screen (§4.1). Plain ground, no card. */
export function NarrativeBlock({ narrative }: { narrative: Narrative }) {
  return (
    <section className="narrative" aria-label={he.now.narrativeTitle}>
      <h2 className="sr-only">{he.now.narrativeTitle}</h2>
      <Sentence sentence={narrative.headline} className="fk-lede" />
      {narrative.lines.map((line) => (
        <Sentence key={line.key} sentence={line} className="fk-body" />
      ))}
    </section>
  );
}

// ───────────────────────────── the urgency row (§4.3) ─────────────────────────────

/**
 * insight-contract §5.4 — the semantic icon keys, mapped to this app's pictures. Where the
 * drawn kit of REDESIGN §7b has a glyph it wins; the four keys it does not carry fall back to
 * the app's own `Icon.tsx`, which draws at the same 24 viewBox and the same 1.75 stroke, so
 * both sit identically inside the circle.
 */
const KIT_GLYPH: Partial<Record<UrgencyIcon, GlyphName>> = {
  card: 'card',
  alert: 'alert',
  receipt: 'receipt',
  invoice: 'receipt',
  loan: 'loan',
  tax: 'landmark',
  'standing-order': 'transfer',
  coverage: 'coins',
};

const APP_ICON: Partial<Record<UrgencyIcon, string>> = {
  trend: 'trend',
  clock: 'clock',
  link: 'link',
  wallet: 'wallet',
};

export function UrgencyGlyph({ icon, severity }: { icon: UrgencyIcon; severity: 'info' | 'warn' | 'critical' }) {
  const glyph = KIT_GLYPH[icon];
  return (
    <span className={`uic ${severity}`} aria-hidden="true">
      {glyph ? <KitCategoryIcon glyph={glyph} size={20} /> : <Icon name={APP_ICON[icon] ?? 'info'} size={20} />}
    </span>
  );
}

/**
 * The ONE row of `עכשיו` / `השבוע` / `החודש` / `אפשר לחכות`: icon, a title in words, the date
 * with its countdown chip, the amount and — where one exists — the action link.
 */
export function UrgencyRow({ item }: { item: UrgencyItem }) {
  const stripe = item.late ? ' fk-severity' : item.severity === 'critical' ? ' fk-severity' : item.severity === 'warn' ? ' fk-severity fk-severity--warn' : '';
  const body = (
    <>
      {item.providerId ? (
        <ProviderMark providerId={item.providerId} size={28} />
      ) : (
        <UrgencyGlyph icon={item.icon} severity={item.severity} />
      )}
      <span className="main">
        <span className="name">{item.title}</span>
        <span className="sub">
          {/* REDESIGN §3 `dd.mm`, zero-padded, from the SAME helper the narrative uses — so the
              date on a row and the date in the sentence above it can never disagree. */}
          {item.date ? <span className="num">{ddmm(item.date)}</span> : null}
          {item.date && item.countdown ? ' · ' : null}
          {item.countdown ? (
            <Chip tone={item.late ? 'critical' : item.severity === 'warn' ? 'warn' : 'muted'}>{item.countdown}</Chip>
          ) : null}
        </span>
      </span>
      {item.amountAgorot === null ? null : (
        <span className="amt">
          <span className="num tab">{fmtMoney(item.amountAgorot)}</span>
        </span>
      )}
      {item.actionHref ? <Icon name="chevL" className="xs t3" /> : null}
    </>
  );
  if (!item.actionHref) {
    return <div className={`listrow urow${stripe}`}>{body}</div>;
  }
  return (
    <Link className={`listrow urow clickable rowlink${stripe}`} to={item.actionHref}>
      {body}
    </Link>
  );
}

// ───────────────────────────── the bucket sections (§4.3–§4.5) ─────────────────────────────

/**
 * `עכשיו` renders in full; `השבוע` and `החודש` collapse to their first 3 rows with `עוד N`
 * (§4.4). A bucket the server dropped never reaches this component.
 */
export function UrgencySection({
  bucket,
  label,
  items,
  limit,
}: {
  bucket: UrgencyBucket;
  label: string;
  items: readonly UrgencyItem[];
  limit?: number;
}) {
  const [open, setOpen] = useState(false);
  const shown = limit && !open ? items.slice(0, limit) : items;
  const hidden = items.length - shown.length;
  const headingId = `urg-${bucket}`;
  return (
    <section className="fk-section urgency" aria-labelledby={headingId}>
      <h2 className="fk-section-title" id={headingId}>
        {label}
      </h2>
      <div className="hairlist">
        {shown.map((item) => (
          <UrgencyRow key={item.id} item={item} />
        ))}
      </div>
      {hidden > 0 || (limit && open && items.length > limit) ? (
        <button type="button" className="link moretoggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? he.now.less : plural(hidden, he.now.moreOne, he.now.moreMany(hidden))}
          <Icon name={open ? 'chevU' : 'chevD'} className="xs" />
        </button>
      ) : null}
    </section>
  );
}

/** `אפשר לחכות` — one collapsed line that opens in place (§4.5). */
export function LaterSection({ items }: { items: readonly UrgencyItem[] }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <section className="fk-section urgency later" aria-labelledby="urg-later">
      <h2 className="sr-only" id="urg-later">
        {URGENCY_BUCKET_LABELS.later}
      </h2>
      <button type="button" className="laterline" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <Icon name="clock" className="s t3" />
        <span>{plural(items.length, he.now.laterOne, he.now.laterMany(items.length))}</span>
        <span className="link">
          {open ? he.now.hide : he.now.show}
          <Icon name={open ? 'chevU' : 'chevD'} className="xs" />
        </span>
      </button>
      {open ? (
        <div className="hairlist">
          {items.map((item) => (
            <UrgencyRow key={item.id} item={item} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

/** A quiet-detail block of §4.6: heading, at most 5 lines of content, one link to its screen. */
export function StripBlock({
  title,
  href,
  linkLabel,
  children,
}: {
  title: string;
  href: string;
  linkLabel: string;
  children: ReactNode;
}) {
  return (
    <section className="stripblock">
      <div className="sec-h">
        <h3>{title}</h3>
        <Link className="link" to={href}>
          {linkLabel}
          <Icon name="chevL" className="xs" />
        </Link>
      </div>
      <div className="stripbody">{children}</div>
    </section>
  );
}

/** A figure inside a strip block or a screen header: value + the comparison §8 requires. */
export function Figure({ value, note, label }: { value: ReactNode; note?: ReactNode; label?: ReactNode }) {
  return (
    <div className="fk-figure">
      {label ? <span className="fk-meta">{label}</span> : null}
      <span className="fk-figure__value">{value}</span>
      {note ? <span className="fk-figure__note">{note}</span> : null}
    </div>
  );
}
